/* ============================================================
   Freebird Stores — Cart cross-sell "Added" state
   ============================================================
   DEV-133. Paige Beallias, 2026-09-14: a suggested product stays in
   the cross-sell row after it is added, "almost has the customer
   wondering 'did I successfully add that additional product'".

   The answer has two halves and they pull against each other:

     snippets/freebird-cart-cross-sell.liquid  hides a product the cart
                                               already holds, so a
                                               stale offer never comes
                                               back on a later view.
     this file                                 keeps the tile the
                                               shopper JUST added on
                                               screen, marked added, so
                                               nothing vanishes under
                                               their finger.

   ── THE TWO SURFACES DO NOT BEHAVE THE SAME, AND ASSUMING THEY DID
   ── WAS THE FIRST VERSION'S BUG ───────────────────────────────────
   The cart DRAWER's cross-sell is inside the drawer's own section, so
   it re-renders on every cart change and the Liquid filter deletes an
   added tile immediately.

   The cart PAGE's cross-sell is a SEPARATE section — `freebird-cart-cross-sell`
   in templates/cart.json. Base re-renders `cart-items-component`, not
   the whole page, so this row is rendered once on load and never
   again while the shopper stays on it.

   The first version inferred "the add landed" from the tile
   disappearing. On the page the tile never disappears, so nothing was
   ever marked added — and every assertion around it passed anyway,
   because a tile that was never removed is indistinguishable from one
   that was removed and restored. A browser run caught it; theme check,
   a rendered-HTML check and a cascade review all could not.

   So the confirmation signal is the cart itself, read once per cart
   change. That is one request per mutation, which is ordinary. The
   thing
   docs/solutions/theme-architecture/reconciling-a-third-party-app-that-writes-to-the-cart.md
   warns against is a POLLING LOOP — 22 requests over nine seconds on a
   back-navigation — not a read per change.

   ── WHAT EACH SIGNAL IS FOR ───────────────────────────────────────
     'cart:update'      -> re-read the cart, then reconcile. This is
                           what decides added vs not-added.
     MutationObserver   -> re-apply the decision to the DOM after a
                           re-render replaced the controls. Reads
                           nothing, decides nothing.

   Both are debounced on a trailing timer rather than
   requestAnimationFrame: a section swap is several mutations and a
   single frame can land between them, on a document momentarily
   holding neither row.

   ── SCOPES ARE SEPARATE ───────────────────────────────────────────
   On /cart the page's row and the drawer's row are both in the
   document. Closing the drawer ends the drawer's added state and must
   not touch the page's, so state is keyed on `data-fb-xs-scope`.

   Navigating away ends both for free — this state is memory and the
   document is gone. That is deliberate: sessionStorage would survive a
   drawer close and a navigation, which is exactly what the requirement
   says must not happen.
   ============================================================ */

const SCOPE_SELECTOR = '[data-fb-xs-scope]';
const CARD_SELECTOR = '[data-fb-xs-product]';
const ADD_SELECTOR = '[data-fb-xs-add]';
const ADDED_ATTR = 'data-fb-xs-added';

/**
 * @typedef {object} CacheEntry
 * @property {string} cardHtml  The tile's markup, captured before any re-render destroyed it.
 * @property {number} cardIndex Its position among the row's tiles, so restoring does not reshuffle the row.
 * @property {string} rowHtml   The row element with its tiles emptied out.
 * @property {string} rowAnchor A selector for the row's parent, for when the row itself is gone.
 * @property {number} rowIndex  The row's position among that parent's children.
 */

/** @type {Map<string, {pending: Map<string, CacheEntry>, added: Map<string, CacheEntry>}>} */
const scopes = new Map();

function stateFor(scope) {
  let state = scopes.get(scope);
  if (!state) {
    state = { pending: new Map(), added: new Map() };
    scopes.set(scope, state);
  }
  return state;
}

/**
 * A selector that finds this element again after its subtree has been
 * replaced. Live element references do not survive a section re-render
 * — the element object itself is discarded — so the anchor has to be
 * re-resolvable. Returns null when no id is reachable, which is the
 * signal to skip row restoration rather than guess.
 */
function describeAnchor(element) {
  const parts = [];
  let node = element;

  while (node && node !== document.body) {
    if (node.id) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      return parts.join(' > ');
    }
    const parent = node.parentElement;
    if (!parent) break;
    const index = Array.prototype.indexOf.call(parent.children, node) + 1;
    parts.unshift(`${node.localName}:nth-child(${index})`);
    node = parent;
  }

  return null;
}

/**
 * Capture everything needed to put this tile back, including the row.
 *
 * THE ROW IS CACHED TOO, and that is not belt-and-braces. In the
 * drawer, a shopper adding the last product still on offer leaves the
 * server with nothing to render, so the row element disappears
 * entirely rather than coming back one tile shorter — "nothing means
 * nothing" doing exactly its job. Restoring only the tile would have
 * nowhere to put it, and the shopper's last add would be the one that
 * looked like it failed.
 */
function cacheCard(card, row) {
  const cards = Array.prototype.slice.call(row.querySelectorAll(CARD_SELECTOR));
  const parent = row.parentElement;

  const rowShell = row.cloneNode(true);
  rowShell.querySelectorAll(CARD_SELECTOR).forEach((node) => node.remove());

  return {
    cardHtml: card.outerHTML,
    cardIndex: cards.indexOf(card),
    rowHtml: rowShell.outerHTML,
    rowAnchor: parent ? describeAnchor(parent) : null,
    rowIndex: parent ? Array.prototype.indexOf.call(parent.children, row) : -1,
  };
}

function elementFromHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.firstElementChild;
}

function markAdded(card) {
  const control = card.querySelector(ADD_SELECTOR);
  if (!control || control.hasAttribute(ADDED_ATTR)) return;

  control.setAttribute(ADDED_ATTR, '');

  // The accessible name comes from the markup, never from a string
  // built here — see the comment on the button in
  // snippets/freebird-cart-cross-sell.liquid. A name composed in
  // JavaScript would be English on every storefront.
  const label = control.getAttribute('data-fb-xs-label-added');
  if (label) control.setAttribute('aria-label', label);
}

function clearAdded(card) {
  const control = card.querySelector(ADD_SELECTOR);
  if (!control || !control.hasAttribute(ADDED_ATTR)) return;

  control.removeAttribute(ADDED_ATTR);
  const label = control.getAttribute('data-fb-xs-label-add');
  if (label) control.setAttribute('aria-label', label);
}

/** Put back a row the server no longer renders, so a restored tile has a home. */
function restoreRow(entry) {
  if (!entry.rowAnchor) return null;

  const parent = document.querySelector(entry.rowAnchor);
  if (!parent) return null;

  const row = elementFromHtml(entry.rowHtml);
  if (!row) return null;

  parent.insertBefore(row, parent.children[entry.rowIndex] || null);
  return row;
}

function restoreCard(row, entry) {
  const card = elementFromHtml(entry.cardHtml);
  if (!card) return;

  const list = row.querySelector('ul') || row;
  const siblings = Array.prototype.slice.call(row.querySelectorAll(CARD_SELECTOR));
  list.insertBefore(card, siblings[entry.cardIndex] || null);
  markAdded(card);
}

/**
 * Put the decided state onto the DOM. Decides nothing itself — that is
 * `refresh()`'s job — so it is safe to run on every mutation.
 */
function apply() {
  scopes.forEach((state, scope) => {
    if (!state.added.size) return;

    let row = document.querySelector(`[data-fb-xs-scope="${scope}"]`);
    if (!row) {
      row = restoreRow(state.added.values().next().value);
      if (!row) return;
    }

    state.added.forEach((entry, id) => {
      const card = row.querySelector(`[data-fb-xs-product="${id}"]`);
      if (card) markAdded(card);
      else restoreCard(row, entry);
    });
  });
}

/**
 * Read the cart once and decide what is added.
 *
 * A pending click resolves here and only here: in the cart means the
 * add landed, absent means it did not (the last unit sold between
 * render and click). This runs AFTER the cart event, so the cart it
 * reads is already the post-add cart and there is no race to wait out.
 */
async function refresh() {
  if (!Array.from(scopes.values()).some((s) => s.pending.size || s.added.size)) return;

  let items;
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    items = (await response.json()).items || [];
  } catch {
    // A failed read is one lost attempt, not a reason to drop state —
    // the next cart change tries again, and the marks currently on
    // screen stay as they are rather than flickering off.
    return;
  }

  const inCart = new Set(items.map((item) => String(item.product_id)));

  scopes.forEach((state) => {
    state.pending.forEach((entry, id) => {
      if (inCart.has(id)) state.added.set(id, entry);
      state.pending.delete(id);
    });

    state.added.forEach((entry, id) => {
      if (inCart.has(id)) return;

      // Removed from the cart while the view is still open. Drop the
      // state and put the control back to its add appearance. The tile
      // is already on screen on the page surface; in the drawer the
      // server will render it again on the next pass.
      state.added.delete(id);
      const card = document.querySelector(`${SCOPE_SELECTOR} [data-fb-xs-product="${id}"]`);
      if (card) clearAdded(card);
    });
  });

  apply();
}

function debounce(fn, wait) {
  let timer = null;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, wait);
  };
}

const queueRefresh = debounce(refresh, 200);
const queueApply = debounce(apply, 150);

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Delegated on document because both surfaces replace their controls
  // on re-render, and a directly bound listener dies at the first one.
  const control = target.closest(ADD_SELECTOR);
  if (!control) return;

  const card = control.closest(CARD_SELECTOR);
  const row = control.closest(SCOPE_SELECTOR);
  if (!card || !row) return;

  const scope = row.getAttribute('data-fb-xs-scope');
  const id = card.getAttribute('data-fb-xs-product');
  if (!scope || !id) return;

  // Cached BEFORE the add, because the drawer's re-render destroys this
  // element and there is no second chance to read its markup.
  stateFor(scope).pending.set(id, cacheCard(card, row));
});

// Add and update share the event name 'cart:update', and this needs
// both — an add to confirm, a removal to revert — so there is no source
// filter here. `refresh()` decides from the cart, not from the event.
document.addEventListener('cart:update', queueRefresh);

// The cart drawer is a <dialog> and `close` does not bubble, so capture
// is the only phase that sees it from here. Scoped to the cart's own
// dialog so the account drawer closing does not clear this state.
document.addEventListener(
  'close',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.classList.contains('cart-drawer__dialog')) return;
    scopes.delete('drawer');
  },
  true
);

// Re-apply after a re-render replaced the controls. This only paints a
// decision already made, so it cannot fight `refresh()`.
new MutationObserver(queueApply).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
